import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, Calculator, ArrowRight, Gauge, Fuel, Cog, Calendar } from "lucide-react";
import { resolveImageUrl } from "../lib/api";
import { buildWhatsappUrl, openWhatsapp, WA_MESSAGES, phoneHref, formatEUR, formatKM } from "../lib/contact";

export const VehicleCard = ({ vehicle, index = 0 }) => {
  const name = `${vehicle.make} ${vehicle.model}`;
  const img = vehicle.images?.[0]
    ? resolveImageUrl(vehicle.images[0])
    : "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=80";

  return (
    <article
      data-testid={`vehicle-card-${vehicle.id}`}
      className="bg-[#2E2E3C] border border-silver-soft rounded-2xl overflow-hidden flex flex-col group transition-all duration-300 hover:-translate-y-1 card-glow"
      style={{ animation: `fadeInUp 0.6s ${index * 0.05}s ease-out both` }}
    >
      <Link
        to={`/fahrzeuge/${vehicle.id}`}
        className="aspect-[16/10] w-full overflow-hidden relative bg-[#1A1A22] block"
        data-testid={`vehicle-card-image-${vehicle.id}`}
      >
        <img
          src={img}
          alt={name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
        {vehicle.badge && (
          <span
            data-testid={`vehicle-badge-${vehicle.id}`}
            className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 bg-[var(--na-red)] text-white rounded-xl"
          >
            {vehicle.badge}
          </span>
        )}
        {vehicle.status === "reserved" && (
          <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 bg-yellow-500/90 text-black rounded-xl">
            Reserviert
          </span>
        )}
        {vehicle.status === "sold" && (
          <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 bg-zinc-700 text-white rounded-xl">
            Verkauft
          </span>
        )}
      </Link>

      <div className="p-5 lg:p-6 flex flex-col gap-4 flex-grow">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              {vehicle.make}
            </p>
            <h3 className="font-display text-lg lg:text-xl font-semibold text-white mt-1 leading-tight">
              {vehicle.model}
            </h3>
          </div>
          <div className="text-right">
            <p className="font-display text-xl lg:text-2xl font-bold text-white tracking-tight">
              {formatEUR(vehicle.price)}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              inkl. 20% USt.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-2 text-[11px] uppercase tracking-wider text-zinc-400 pt-2 border-t border-white/5">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{vehicle.year}</span>
          <span className="inline-flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" />{formatKM(vehicle.mileage)}</span>
          <span className="inline-flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5" />{vehicle.fuel}</span>
          <span className="inline-flex items-center gap-1.5"><Cog className="w-3.5 h-3.5" />{vehicle.transmission}</span>
        </div>

        <div className="mt-auto grid grid-cols-2 gap-2 pt-3">
          <Link
            to={`/fahrzeuge/${vehicle.id}`}
            data-testid={`vehicle-details-link-${vehicle.id}`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white text-[#1A1A22] text-sm font-medium hover:bg-zinc-200 transition-colors"
          >
            Details <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to={`/finanzierung?vehicle=${vehicle.id}`}
            data-testid={`vehicle-financing-link-${vehicle.id}`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-white/15 text-sm text-white hover:bg-white/5 transition-colors"
          >
            <Calculator className="w-3.5 h-3.5" /> Finanzierung
          </Link>
          <a
            href={buildWhatsappUrl(WA_MESSAGES.vehicleInterest(name))}
            onClick={openWhatsapp(WA_MESSAGES.vehicleInterest(name))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`vehicle-whatsapp-link-${vehicle.id}`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#25D366] text-[#1A1A22] text-sm font-medium hover:bg-[#20bd5a] transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
          <a
            href={phoneHref}
            data-testid={`vehicle-phone-link-${vehicle.id}`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-white/15 text-sm text-white hover:bg-white/5 transition-colors"
          >
            <Phone className="w-3.5 h-3.5" /> Anrufen
          </a>
        </div>
      </div>
    </article>
  );
};

export default VehicleCard;
